import { ContainerFeature, Plan } from '@back4app2/sdk/lib/Back4app2';
import { ReactComponent as Spinner } from '../assets/images/status-spinner.svg';
import { DsButton } from '../components/ds';

/**
 * The two things that are bought on the configure step: the instance the app
 * runs on, and the extras sold per app on top of it. They land on the same
 * subscription as separate line items.
 *
 * Split in two on purpose. The instance size, the line items and the total are
 * the RAIL — pinned beside the review, because the price has to be visible
 * while the settings beside it are being read. The extras are a CARD in the
 * review column, because adding a database is a decision about the app, not a
 * number on the invoice.
 *
 * Neither renders a button: the form owns the single Create that finishes the
 * flow, and `containerTotal` is exported so the two cannot disagree about what
 * is being charged.
 */

/** Bytes to the shortest honest label. 1073741824 -> "1 GB". */
const formatStorage = (bytes?: number | null): string | null => {
  if (!bytes) {
    return null;
  }

  const gb = bytes / 1024 ** 3;
  if (gb >= 1) {
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }

  return `${Math.round(bytes / 1024 ** 2)} MB`;
};

export const formatMoney = (amount: number): string => `$${amount.toFixed(2)}`;

/**
 * What is actually billed, given a plan and the extras kept with it.
 *
 * Exported because the line that names the charge lives in the form, not here.
 * Two additions of the same numbers in two files stay equal only until one of
 * them learns about a discount.
 */
export const containerTotal = (
  plan: Plan | undefined,
  features: ContainerFeature[],
  selectedFeatureIds: string[]
): number =>
  (plan?.price || 0) +
  features
    .filter(
      (feature) =>
        selectedFeatureIds.includes(feature.id) &&
        (!plan || feature.planIds.includes(plan.id))
    )
    .reduce((sum, feature) => sum + feature.price, 0);

/**
 * A plan in one line, in the platform's own terms.
 *
 * The Free difference is that its URL EXPIRES, not that it sleeps. Both are
 * true, but they are not the same promise: sleeping is transparent — the app
 * wakes on the next request. Expiry is terminal — `handleExpiredCustomDomains`
 * disables the balancer rule, destroys the deployments and marks the domain
 * EXPIRED, and the app stays off until the user upgrades. That is the thing
 * someone choosing a plan has to know.
 *
 * It says THAT it expires, not when. `urlExpiresInMinutes` is still the
 * condition — it comes from the server, so it appears exactly on the plans that
 * really expire — but the number stays out of the line: it is a platform
 * setting that differs per environment, and one more figure next to a price and
 * two specs is a line nobody finishes reading.
 */
const planSpecs = (plan: Plan): string =>
  [
    `${plan.maxCPU} vCPU`,
    `${plan.maxRAM} MB`,
    plan.urlExpiresInMinutes ? 'URL expires' : 'always on',
  ].join('  ·  ');

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300">
    {children}
  </span>
);

interface PlanOptionProps {
  plan: Plan;
  isSelected: boolean;
  onSelect: () => void;
  /** Shown inside the option, and only while it is the selected one. */
  blockedMessage?: string;
}

/**
 * Compact by design: in the rail there is no room for a feature table, and
 * there is nothing to compare anyway — the plans differ by two numbers and one
 * promise, which is exactly what the line under the name says.
 */
const PlanOption = ({ plan, isSelected, onSelect, blockedMessage }: PlanOptionProps) => (
  <label
    className={`block px-4 py-3 rounded-card border cursor-pointer transition-colors duration-[120ms] ${
      isSelected
        ? 'bg-action/[0.10] border-action'
        : 'bg-navy-700 border-transparent hover:border-navy-400'
    }`}
  >
    {/* The real control, visually hidden rather than removed: it keeps the
        group keyboard-navigable and the label clickable. */}
    <input
      type="radio"
      name="container-plan"
      className="sr-only"
      checked={isSelected}
      onChange={onSelect}
    />
    <span className="flex items-baseline justify-between gap-3">
      <span className="font-sora text-ds-135 font-semibold text-white">
        {plan.name}
      </span>
      <span className="font-sora text-ds-135 font-semibold text-white">
        {plan.price === 0 ? '$0' : `$${plan.price}`}
      </span>
    </span>
    <span className="mt-1 block font-courrier text-ds-115 text-navy-200">
      {planSpecs(plan)}
    </span>

    {/* Inside the option, under what it is about — not beside the button,
        where it would read as a complaint about the whole form rather than
        about the plan that causes it. */}
    {isSelected && blockedMessage ? (
      <span className="mt-2 block text-ds-115 leading-[1.5] text-state-warn">
        {blockedMessage}
      </span>
    ) : null}
  </label>
);

const LineItem = ({ label, amount }: { label: string, amount: number }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="min-w-0 truncate text-ds-125 text-navy-200">{label}</span>
    <span className="flex-none font-courrier text-ds-125 text-navy-100">
      {formatMoney(amount)}
    </span>
  </div>
);

export interface InstanceSizePanelProps {
  plans: Plan[];
  features: ContainerFeature[];
  selectedPlanId?: string;
  selectedFeatureIds: string[];
  onSelectPlan: (planId: string) => void;
  /** The catalog is fetched when the step opens; until it lands there is
   * nothing to choose between, and empty rows would read as "no plans". */
  isLoading?: boolean;
  errorMessage?: string;
  /**
   * The account's free allowance, from the server. When it is spent, the Free
   * option says so — and only the Free option: a paid plan does not consume a
   * free slot, so warning about it anywhere else would be warning about a limit
   * that does not apply to what is being bought.
   */
  freeAppsUsed?: number;
  maxFreeApps?: number | null;
}

export const InstanceSizePanel = ({
  plans,
  features,
  selectedPlanId,
  selectedFeatureIds,
  onSelectPlan,
  isLoading = false,
  errorMessage,
  freeAppsUsed,
  maxFreeApps,
}: InstanceSizePanelProps) => {
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);

  const chosenFeatures = features.filter(
    (feature) =>
      selectedFeatureIds.includes(feature.id) &&
      (!selectedPlan || feature.planIds.includes(selectedPlan.id))
  );

  const total = containerTotal(selectedPlan, features, selectedFeatureIds);

  // Only a free plan consumes a free slot, so this is the only option it can be
  // about. `>=` and not `===`: an account can end up over the limit — a paid app
  // that goes back to Free, or a limit that was lowered — and it must still say
  // the allowance is spent rather than go quiet.
  const freeAllowanceMessage =
    typeof maxFreeApps === 'number' &&
    typeof freeAppsUsed === 'number' &&
    freeAppsUsed >= maxFreeApps
      ? `You're using all ${maxFreeApps} of your free web apps. Delete one, or move an app to a paid plan, to deploy another.`
      : undefined;

  if (isLoading) {
    return (
      <div className="px-5 py-16 flex items-center justify-center">
        <Spinner className="animate-spin" width="20px" height="20px" />
      </div>
    );
  }

  return (
    <div>
      <div className="px-5 pt-4 pb-3">
        <SectionLabel>instance size</SectionLabel>
      </div>

      <div className="px-3 pb-3 flex flex-col gap-2">
        {plans.map((plan) => (
          <PlanOption
            key={plan.id}
            plan={plan}
            isSelected={plan.id === selectedPlanId}
            onSelect={() => onSelectPlan(plan.id)}
            blockedMessage={plan.price === 0 ? freeAllowanceMessage : undefined}
          />
        ))}
      </div>

      {/* What the total is made of, only once there is a plan to make it of. */}
      <div className="px-5 py-4 border-t border-navy-600 flex flex-col gap-2.5">
        {selectedPlan ? (
          <LineItem
            label={`${selectedPlan.name} instance`}
            amount={selectedPlan.price}
          />
        ) : null}
        {chosenFeatures.map((feature) => (
          <LineItem key={feature.id} label={feature.name} amount={feature.price} />
        ))}
      </div>

      <div className="px-5 py-4 border-t border-navy-600 flex items-baseline justify-between gap-3">
        <span className="text-ds-135 text-white">Total</span>
        <span className="flex items-baseline gap-1.5">
          <span className="font-sora text-[26px] leading-none font-semibold text-white">
            {formatMoney(total)}
          </span>
          <span className="text-ds-115 text-navy-200">/ month</span>
        </span>
      </div>

      {errorMessage ? (
        <p className="m-0 px-5 pb-4 text-ds-115 text-state-err">{errorMessage}</p>
      ) : null}
    </div>
  );
};

export interface FeaturesCardProps {
  features: ContainerFeature[];
  plans: Plan[];
  selectedPlanId?: string;
  selectedFeatureIds: string[];
  onToggleFeature: (featureId: string) => void;
  isLoading?: boolean;
}

/**
 * The extras, as the last card of the review.
 *
 * Shown, and addable, even when the chosen plan cannot buy them: Add then
 * moves the app to the cheapest plan that sells the extra, and a quiet line
 * says so beforehand. Hiding or locking them while Free is selected made the
 * person who came here to add a database find the plan that unlocks it on
 * their own.
 */
export const FeaturesCard = ({
  features,
  plans,
  selectedPlanId,
  selectedFeatureIds,
  onToggleFeature,
  isLoading = false,
}: FeaturesCardProps) => {
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);

  if (isLoading || !features.length) {
    return null;
  }

  const availableCount = features.filter(
    (feature) => !selectedPlan || feature.planIds.includes(selectedPlan.id)
  ).length;

  return (
    <section className="rounded-card bg-navy-800 border border-navy-500 overflow-hidden">
      <header className="px-5 py-3.5 flex items-baseline justify-between gap-3 border-b border-navy-600">
        <h2 className="m-0 font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
          Features
        </h2>
        <span className="text-ds-115 text-navy-300">
          {availableCount ? 'Billed with your plan' : 'Available on paid plans'}
        </span>
      </header>

      <div className="px-5">
        {features.map((feature) => {
          // The catalog carries the gate; the purchase path re-checks it
          // server-side — this is a courtesy, not a control.
          const isAvailable =
            !selectedPlan || feature.planIds.includes(selectedPlan.id);
          const isSelected = selectedFeatureIds.includes(feature.id);
          const storage = formatStorage(feature.maxStorageBytes);
          // Where Add takes the plan when the chosen one does not sell this.
          const upgradePlan = isAvailable
            ? undefined
            : plans
              .filter((plan) => plan.price > 0 && feature.planIds.includes(plan.id))
              .sort((a, b) => a.price - b.price)[0];

          return (
            <div
              key={feature.id}
              className="py-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b border-navy-700 last:border-b-0"
            >
              {/* Full width on a phone, with the price and the button on a row
                  of their own below: beside them the description had a third of
                  the card and wrapped one word per line. */}
              <div className="min-w-0 flex-1 basis-full sm:basis-0">
                <div className="font-sora text-ds-135 font-semibold text-white">
                  {feature.name}
                </div>
                {feature.description || storage ? (
                  <p className="m-0 mt-1 text-ds-115 leading-[1.5] text-navy-200">
                    {feature.description}
                    {feature.description && storage ? ' ' : null}
                    {storage ? `Includes ${storage} storage.` : null}
                  </p>
                ) : null}
                {/* Quiet: it can still be added — Add picks the plan. "Available
                    on paid plans" is already the card's header. */}
                {!isAvailable && upgradePlan ? (
                  <p className="m-0 mt-1 text-ds-115 leading-[1.5] text-navy-300">
                    Adding it selects {upgradePlan.name}.
                  </p>
                ) : null}
              </div>

              <div className="flex-none flex items-center justify-between gap-3 w-full sm:w-auto">
                <span className="font-courrier text-ds-125 text-navy-200">
                  +${feature.price} / mo
                </span>
                <DsButton
                  variant={isSelected ? 'subtle' : 'outline'}
                  size="md"
                  onClick={() => onToggleFeature(feature.id)}
                  isDisabled={!isAvailable && !upgradePlan}
                >
                  {!isAvailable && !upgradePlan ? 'Not available' : isSelected ? 'Remove' : 'Add'}
                </DsButton>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
